// 远程语言包 —— 拉取 / 解析 / 与本地包合并 / 会话内缓存
//
// 背景：PolyglotEnJson / PolyglotZhJson / PolyglotJaJson 原来只随前端发版更新，
// 运营改了语言包要等前端重新构建才生效。这里在运行时按团队已开通语言拉取 web 端语言包
// （与语言包「编辑文件」用的是同一个接口 /api/admin/ml/getDataByPort），与本地包合并：
// **远程优先、本地兜底**，并缓存到 sessionStorage——本项目是多页应用，切页面会重建 JS
// 上下文，只有 sessionStorage 能保证缓存不丢。
import { getDataByPort } from "@api/lang";
import { getLocalPack } from "./lang_packs";
import { teamCodes } from "./teamLang";

// 语言包文件挂在 web 端口下（与语言包编辑弹窗一致）
const PACK_PORT_TYPE = "web";
// sessionStorage key：{ [语言码]: 远程语言包对象 }
const STORAGE_KEY = "remote_lang_packs";

const listeners = new Set();
let loadingPromise = null;

const readCache = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch (error) {
    return {};
  }
};

let remotePacks = readCache();

const writeCache = (nextPacks) => {
  remotePacks = nextPacks;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(nextPacks));
  } catch (error) {
    // 写入超限等异常不影响本次会话的内存缓存
  }
};

// 远程语言包是 JS 模块写法：export const PolyglotEnJson = {...}
// 优先按 JSON 解析（后台「导入当前项目配置」生成的就是标准 JSON），
// 解析失败再退化为表达式求值，兼容运营手工上传的写法。
export const parseRemotePack = (source) => {
  const text = String(source || "");
  const equalIndex = text.indexOf("=");
  if (equalIndex < 0) return null;
  const body = text.slice(equalIndex + 1).trim().replace(/;+\s*$/, "");
  try {
    return JSON.parse(body);
  } catch (error) {
    try {
      // eslint-disable-next-line no-new-func
      return new Function(`return (${body});`)();
    } catch (fallbackError) {
      return null;
    }
  }
};

// 合并后的语言包：远程优先、本地兜底
export const getTranslationPack = (code) => {
  const localPack = getLocalPack(code) || {};
  const remotePack = remotePacks[code];
  return remotePack ? { ...localPack, ...remotePack } : localPack;
};

// 订阅远程包到位事件（返回取消订阅函数），供 i18n 覆盖与组件重渲染使用
export const subscribeRemoteLanguagePacks = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const emit = (code, pack) => listeners.forEach((listener) => listener(code, pack));

// 拉取远程语言包：默认取当前团队已开通语言；已缓存的跳过；并发调用复用同一个 Promise
export const ensureRemoteLanguagePacks = ({ codes, force } = {}) => {
  if (loadingPromise) return loadingPromise;
  const targetCodes = (codes && codes.length ? codes : teamCodes()) || [];
  loadingPromise = Promise.all(
    targetCodes.map(async (code) => {
      if (!force && remotePacks[code]) return;
      try {
        const res = await getDataByPort({ languageCode: code, portType: PACK_PORT_TYPE });
        const fileUrl = res?.result?.fileUrl;
        if (!res?.success || !fileUrl) return;
        const source = await fetch(fileUrl, { cache: "no-store" }).then((response) => response.text());
        const pack = parseRemotePack(source);
        if (!pack) return;
        writeCache({ ...remotePacks, [code]: pack });
        emit(code, pack);
      } catch (error) {
        // 单个语言失败不影响其它语言
      }
    })
  ).finally(() => {
    loadingPromise = null;
  });
  return loadingPromise;
};
